/**
 * Content & Social - unified pipeline for content requests, scheduled social
 * posts, and email campaign performance.
 *
 * Four sections:
 *   1) Content Requests Kanban   (drag-drop, reuses SEO Workspace pattern)
 *   2) Social Media Calendar     (month grid + slide-over detail)
 *   3) Post Log                  (data table of all social posts)
 *   4) Email Campaign Log        (data table with color-coded rate metrics)
 *
 * Rate color rules (per user spec):
 *   Open rate:  >= 20 green, 10-19.99 amber, < 10 red
 *   Click rate: >=  3 green,  1- 2.99 amber, <  1 red
 */
import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Plus, X, FileText, ChevronLeft, ChevronRight,
  GripVertical, Loader2, Linkedin, Instagram, Twitter, Facebook, Youtube,
  Globe, Megaphone, Mail, Calendar as CalIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { FileUpload } from '@/components/FileUpload';
import { useAuth } from '@/contexts/AuthContext';


// ─── Constants ───────────────────────────────────────────────────────────
const STATUS_COLUMNS = ['Requested', 'In Progress', 'In Review', 'Published', 'On Hold'];
const CONTENT_TYPES = ['Blog Post', 'Newsletter', 'Social Post', 'Landing Page', 'Page Optimization'];
const SOCIAL_STATUSES = ['Draft', 'Scheduled', 'Published'];
const SOCIAL_CHANNELS = ['LinkedIn', 'Instagram', 'Twitter/X', 'Facebook', 'YouTube', 'TikTok', 'Blog', 'Other'];
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MAX_ATTACHMENTS = 3;

const BADGE = {
  contentType: {
    'Blog Post': 'bg-primary/15 text-primary border-primary/30',
    'Newsletter': 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    'Social Post': 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30',
    'Landing Page': 'bg-sky-500/15 text-sky-300 border-sky-500/30',
    'Page Optimization': 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  },
  socialStatus: {
    Draft: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
    Scheduled: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
    Published: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  },
};

// Tailwind border / dot classes per social status - used on calendar tiles.
const SOCIAL_STATUS_BORDER = {
  Draft: 'border-l-slate-500/70 bg-slate-500/10',
  Scheduled: 'border-l-sky-500 bg-sky-500/10',
  Published: 'border-l-emerald-500 bg-emerald-500/10',
};

// Platform icon + accent color.
const CHANNEL_META = {
  LinkedIn: { Icon: Linkedin, color: 'text-[#0A66C2]' },
  Instagram: { Icon: Instagram, color: 'text-[#E1306C]' },
  'Twitter/X': { Icon: Twitter, color: 'text-sky-400' },
  Facebook: { Icon: Facebook, color: 'text-[#1877F2]' },
  YouTube: { Icon: Youtube, color: 'text-red-500' },
  TikTok: { Icon: Megaphone, color: 'text-fuchsia-400' },
  Blog: { Icon: FileText, color: 'text-emerald-400' },
  Other: { Icon: Globe, color: 'text-muted-foreground' },
};

function badgeClass(kind, value) {
  return BADGE[kind]?.[value] || 'bg-secondary text-muted-foreground border-border';
}

function formatDate(iso, opts = { month: 'short', day: 'numeric', year: 'numeric' }) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
}

/** Returns YYYY-MM-DD in UTC for the given Date. */
function toIsoDate(d) {
  return d.toISOString().slice(0, 10);
}

/** Compute UTC-safe day-of-week label from ISO date. */
function dayLabelFromIso(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return DAY_LABELS[d.getUTCDay()];
}


// ═══════════════════════════════════════════════════════════════════════════
// SECTION 1 - Content Requests Kanban (drag & drop)
// ═══════════════════════════════════════════════════════════════════════════
function ContentKanbanCard({ card, onClick, onDragStart, canEdit }) {
  return (
    <div
      role="button"
      tabIndex={0}
      draggable={canEdit}
      onDragStart={canEdit ? (e) => onDragStart(e, card.id) : undefined}
      onClick={() => onClick(card)}
      onKeyDown={(e) => (e.key === 'Enter' ? onClick(card) : null)}
      className={`rounded-md border border-white/10 bg-card p-3 space-y-2 hover:border-primary/40 transition-colors ${canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
      data-testid={`content-kanban-card-${card.id}`}
    >
      <div className="flex items-start gap-2">
        {canEdit && <GripVertical className="h-3.5 w-3.5 text-muted-foreground/50 mt-0.5 shrink-0" aria-hidden />}
        <p className="text-sm font-medium leading-tight text-foreground line-clamp-2 flex-1">
          {card.content_title || 'Untitled'}
        </p>
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Badge variant="outline" className={`text-[10px] uppercase ${badgeClass('contentType', card.content_type)}`}>
          {card.content_type || 'Blog Post'}
        </Badge>
        {card.due_date && (
          <span className="text-[11px] text-muted-foreground font-mono tabular-nums">
            {formatDate(card.due_date, { month: 'short', day: 'numeric' })}
          </span>
        )}
      </div>
      {card.assigned_to && (
        <p className="text-[11px] text-muted-foreground">
          <span className="text-muted-foreground/70">Assigned:</span>{' '}
          <span className="text-foreground/90">{card.assigned_to}</span>
        </p>
      )}
      {(card.attachments || []).length > 0 && (
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground/80">
          <FileText className="h-3 w-3" />
          {card.attachments.length} attachment{card.attachments.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}


function ContentKanban({ cards, canEdit, onCardClick, onCardStatusChange }) {
  const [dragOverCol, setDragOverCol] = useState(null);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(STATUS_COLUMNS.map(s => [s, []]));
    for (const c of cards) {
      if (g[c.status]) g[c.status].push(c);
    }
    for (const k of Object.keys(g)) {
      g[k].sort((a, b) => (a.due_date || '9999').localeCompare(b.due_date || '9999'));
    }
    return g;
  }, [cards]);

  const handleDragStart = (e, cardId) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', cardId);
  };
  const handleDrop = (e, targetStatus) => {
    e.preventDefault();
    setDragOverCol(null);
    const id = e.dataTransfer.getData('text/plain');
    if (!id) return;
    const card = cards.find(c => c.id === id);
    if (card && card.status !== targetStatus) {
      onCardStatusChange(id, targetStatus);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="content-social-kanban">
      {STATUS_COLUMNS.map(col => (
        <div
          key={col}
          className={`rounded-md border ${dragOverCol === col ? 'border-primary/60 bg-primary/5' : 'border-white/10 bg-secondary/20'} flex flex-col transition-colors`}
          onDragOver={canEdit ? (e) => { e.preventDefault(); setDragOverCol(col); } : undefined}
          onDragLeave={canEdit ? () => setDragOverCol(null) : undefined}
          onDrop={canEdit ? (e) => handleDrop(e, col) : undefined}
          data-testid={`content-social-kanban-col-${col.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div className="px-3 pt-3 pb-2 flex items-center justify-between border-b border-white/10">
            <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground">{col}</p>
            <span className="text-[11px] font-mono tabular-nums text-muted-foreground/80">{grouped[col].length}</span>
          </div>
          <div className="p-2.5 space-y-2 min-h-[120px] max-h-[520px] overflow-y-auto">
            {grouped[col].length === 0 && (
              <p className="text-[11px] text-muted-foreground/60 italic px-1 py-2">Drop cards here.</p>
            )}
            {grouped[col].map(card => (
              <ContentKanbanCard
                key={card.id}
                card={card}
                canEdit={canEdit}
                onClick={onCardClick}
                onDragStart={handleDragStart}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Content Detail Modal (edit request from Kanban)
// ═══════════════════════════════════════════════════════════════════════════
function ContentDetailModal({ open, card, onClose, onSaved, canEdit, keywords, strategies }) {
  const isNew = open && !card?.id;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showUploader, setShowUploader] = useState(false);

  useEffect(() => {
    if (open) {
      if (isNew) {
        // Default to the active strategy if any, otherwise the most recent.
        const defaultStrategy = strategies.find(s => s.status === 'Active') || strategies[0];
        setForm({
          content_title: '',
          content_type: 'Blog Post',
          brief_notes: '',
          assigned_to: '',
          status: 'Requested',
          due_date: '',
          published_url: '',
          external_reference_url: '',
          attachments: [],
          keyword_id: '',
          monthly_strategy_id: defaultStrategy?.id || '',
        });
      } else if (card) {
        setForm({
          content_title: card.content_title || '',
          content_type: card.content_type || 'Blog Post',
          brief_notes: card.brief_notes || '',
          assigned_to: card.assigned_to || '',
          status: card.status || 'Requested',
          due_date: card.due_date || '',
          published_url: card.published_url || '',
          external_reference_url: card.external_reference_url || '',
          attachments: card.attachments || [],
          keyword_id: card.keyword_id || '',
          monthly_strategy_id: card.monthly_strategy_id || '',
        });
      }
      setShowUploader(false);
    }
  }, [open, card, isNew, strategies]);

  if (!form) return null;

  const handleUploadComplete = (uploaded) => {
    if (form.attachments.length >= MAX_ATTACHMENTS) {
      toast.error(`Maximum ${MAX_ATTACHMENTS} attachments per record.`);
      return;
    }
    setForm(f => ({ ...f, attachments: [...f.attachments, uploaded] }));
    setShowUploader(false);
  };

  const removeAttachment = (idx) => {
    setForm(f => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));
  };

  const submit = async () => {
    if (!form.content_title.trim()) return toast.error('Content Title is required');
    if (!form.keyword_id) return toast.error('Keyword Target is required');
    if (!form.monthly_strategy_id) return toast.error('Monthly Strategy is required');
    setSaving(true);
    try {
      const r = isNew
        ? await api.post('/content-calendar', form)
        : await api.put(`/content-calendar/${card.id}`, form);
      toast.success(isNew ? 'Content request created' : 'Content saved');
      onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const attachmentsFull = form.attachments.length >= MAX_ATTACHMENTS;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="cs-content-modal">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New Content Request' : (canEdit ? 'Edit Content' : 'Content Details')}</DialogTitle>
          <DialogDescription>{isNew ? 'Create a new content request and drop it into the pipeline.' : (canEdit ? 'Update the request and move it through the pipeline.' : 'Read-only view of this content request.')}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormField label="Content Title" testId="csm-title" className="md:col-span-2">
            <Input value={form.content_title} disabled={!canEdit} onChange={(e) => setForm({ ...form, content_title: e.target.value })} className="bg-background border-white/10" data-testid="csm-title-input" />
          </FormField>
          <FormField label="Content Type" testId="csm-type">
            <Select value={form.content_type} onValueChange={(v) => setForm({ ...form, content_type: v })} disabled={!canEdit}>
              <SelectTrigger className="bg-background border-white/10" data-testid="csm-type-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>{CONTENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Status" testId="csm-status">
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })} disabled={!canEdit}>
              <SelectTrigger className="bg-background border-white/10" data-testid="csm-status-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_COLUMNS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Assigned To" testId="csm-assignee">
            <Input value={form.assigned_to} disabled={!canEdit} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="bg-background border-white/10" data-testid="csm-assignee-input" />
          </FormField>
          <FormField label="Due Date" testId="csm-due">
            <Input type="date" value={form.due_date} disabled={!canEdit} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-background border-white/10" data-testid="csm-due-input" />
          </FormField>
          <FormField label="Keyword Target" testId="csm-keyword">
            <Select value={form.keyword_id} onValueChange={(v) => setForm({ ...form, keyword_id: v })} disabled={!canEdit}>
              <SelectTrigger className="bg-background border-white/10" data-testid="csm-keyword-trigger"><SelectValue placeholder="Pick a keyword" /></SelectTrigger>
              <SelectContent>
                {keywords.map(k => <SelectItem key={k.id} value={k.id}>{k.keyword}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Monthly Strategy" testId="csm-strategy">
            <Select value={form.monthly_strategy_id} onValueChange={(v) => setForm({ ...form, monthly_strategy_id: v })} disabled={!canEdit}>
              <SelectTrigger className="bg-background border-white/10" data-testid="csm-strategy-trigger"><SelectValue placeholder="Pick a strategy" /></SelectTrigger>
              <SelectContent>
                {strategies.map(s => <SelectItem key={s.id} value={s.id}>{formatDate(s.month, { month: 'short', year: 'numeric' })} - {s.status}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Brief / Notes" testId="csm-brief" className="md:col-span-2">
            <Textarea rows={4} value={form.brief_notes} disabled={!canEdit} onChange={(e) => setForm({ ...form, brief_notes: e.target.value })} className="bg-background border-white/10" data-testid="csm-brief-input" />
          </FormField>
          <FormField label="Published URL" testId="csm-pub-url" className="md:col-span-2">
            <Input value={form.published_url} disabled={!canEdit} onChange={(e) => setForm({ ...form, published_url: e.target.value })} className="bg-background border-white/10" data-testid="csm-pub-url-input" placeholder="https://..." />
          </FormField>
          <div className="md:col-span-2 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground">
                Attachments <span className="text-muted-foreground/60 normal-case">({form.attachments.length}/{MAX_ATTACHMENTS})</span>
              </Label>
              {canEdit && !attachmentsFull && !showUploader && (
                <Button size="sm" variant="outline" onClick={() => setShowUploader(true)} className="gap-1.5" data-testid="csm-add-attachment-btn">
                  <Plus className="h-3 w-3" /> Add file
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {form.attachments.length === 0 && !showUploader && (
                <p className="text-[11px] text-muted-foreground/60 italic">No attachments yet.</p>
              )}
              {form.attachments.map((a, idx) => (
                <div key={a.file_id || idx} className="flex items-center gap-3 rounded-md border border-white/10 bg-background/40 px-3 py-2" data-testid={`csm-attachment-${idx}`}>
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{a.file_name || a.file_id}</p>
                    {a.file_size ? <p className="text-[10px] text-muted-foreground">{(a.file_size / 1024).toFixed(1)} KB</p> : null}
                  </div>
                  <a href={a.file_url || `/api/files/${a.file_id}`} target="_blank" rel="noreferrer" className="text-[11px] text-primary hover:underline">Open</a>
                  {canEdit && (
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeAttachment(idx)} data-testid={`csm-remove-attachment-${idx}`}>
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
              {showUploader && canEdit && (
                <div className="space-y-2">
                  <FileUpload onUploadComplete={handleUploadComplete} />
                  <Button size="sm" variant="ghost" onClick={() => setShowUploader(false)} className="text-xs">Cancel upload</Button>
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Close</Button>
          {canEdit && (
            <Button onClick={submit} disabled={saving} data-testid="csm-save-btn" className="bg-primary text-primary-foreground gap-1.5">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {isNew ? 'Create Request' : 'Save'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// SECTION 2 - Social Media Calendar (month grid)
// ═══════════════════════════════════════════════════════════════════════════
function buildMonthGrid(anchorDate) {
  // Return array of 6 weeks * 7 days (42 cells) covering the visible month view.
  const y = anchorDate.getUTCFullYear();
  const m = anchorDate.getUTCMonth();
  const firstOfMonth = new Date(Date.UTC(y, m, 1));
  const startWeekday = firstOfMonth.getUTCDay();
  // Start at the Sunday before or equal to the 1st.
  const start = new Date(Date.UTC(y, m, 1 - startWeekday));
  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(Date.UTC(y, m, 1 - startWeekday + i));
    cells.push(d);
  }
  return { cells, monthIndex: m, year: y };
}


function SocialMediaCalendar({ posts, anchor, setAnchor, canEdit, onCellCreate, onPostClick }) {
  const { cells, monthIndex, year } = useMemo(() => buildMonthGrid(anchor), [anchor]);

  // Group posts by posting_date (YYYY-MM-DD).
  const postsByDate = useMemo(() => {
    const map = {};
    for (const p of posts) {
      if (!p.posting_date) continue;
      if (!map[p.posting_date]) map[p.posting_date] = [];
      map[p.posting_date].push(p);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (a.time_of_post || '').localeCompare(b.time_of_post || ''));
    }
    return map;
  }, [posts]);

  const monthLabel = new Date(Date.UTC(year, monthIndex, 1)).toLocaleDateString('en-US', {
    month: 'long', year: 'numeric', timeZone: 'UTC',
  });

  const goto = (delta) => {
    setAnchor(new Date(Date.UTC(year, monthIndex + delta, 1)));
  };

  const today = toIsoDate(new Date());

  return (
    <Card className="border-white/10 bg-card shadow-none" data-testid="social-calendar">
      <CardContent className="p-0">
        {/* Toolbar */}
        <div className="p-4 flex items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-2">
            <Button size="icon" variant="outline" onClick={() => goto(-1)} className="h-8 w-8" data-testid="social-cal-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <p className="text-sm font-medium min-w-[140px] text-center" data-testid="social-cal-month-label">{monthLabel}</p>
            <Button size="icon" variant="outline" onClick={() => goto(1)} className="h-8 w-8" data-testid="social-cal-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAnchor(new Date())} className="text-xs" data-testid="social-cal-today">
              Today
            </Button>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
            <LegendDot label="Draft" swatch="bg-slate-500" />
            <LegendDot label="Scheduled" swatch="bg-sky-500" />
            <LegendDot label="Published" swatch="bg-emerald-500" />
          </div>
        </div>

        {/* Day-of-week header */}
        <div className="grid grid-cols-7 border-b border-white/10">
          {DAY_LABELS.map(d => (
            <div key={d} className="px-2 py-2 text-center text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
              {d}
            </div>
          ))}
        </div>

        {/* Cells */}
        <div className="grid grid-cols-7">
          {cells.map((d, idx) => {
            const iso = toIsoDate(d);
            const dayPosts = postsByDate[iso] || [];
            const inMonth = d.getUTCMonth() === monthIndex;
            const isToday = iso === today;
            return (
              <div
                key={idx}
                className={`min-h-[120px] border-r border-b border-white/5 p-1.5 flex flex-col gap-1 ${inMonth ? 'bg-background/20' : 'bg-background/60'} ${(idx + 1) % 7 === 0 ? 'border-r-0' : ''}`}
                data-testid={`social-cal-cell-${iso}`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-[11px] font-mono tabular-nums ${inMonth ? 'text-foreground/80' : 'text-muted-foreground/40'} ${isToday ? 'bg-primary text-primary-foreground rounded-full h-5 w-5 flex items-center justify-center' : ''}`}
                  >
                    {d.getUTCDate()}
                  </span>
                  {canEdit && inMonth && (
                    <button
                      type="button"
                      onClick={() => onCellCreate(iso)}
                      className="text-muted-foreground/40 hover:text-primary transition-colors"
                      title="New post on this day"
                      data-testid={`social-cal-add-${iso}`}
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div className="flex-1 space-y-1 overflow-hidden">
                  {dayPosts.slice(0, 3).map(p => (
                    <SocialCalendarPill key={p.id} post={p} onClick={() => onPostClick(p)} />
                  ))}
                  {dayPosts.length > 3 && (
                    <button
                      type="button"
                      onClick={() => onPostClick(dayPosts[3])}
                      className="text-[10px] text-primary hover:underline text-left"
                      data-testid={`social-cal-more-${iso}`}
                    >
                      +{dayPosts.length - 3} more
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}


function SocialCalendarPill({ post, onClick }) {
  const meta = CHANNEL_META[post.channel] || CHANNEL_META.Other;
  const IconEl = meta.Icon;
  const borderCls = SOCIAL_STATUS_BORDER[post.status] || SOCIAL_STATUS_BORDER.Draft;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-sm border-l-2 ${borderCls} px-1.5 py-1 hover:bg-white/5 transition-colors flex items-center gap-1.5 min-w-0`}
      data-testid={`social-cal-post-${post.id}`}
      title={`${post.channel || 'Other'} - ${post.status}`}
    >
      <IconEl className={`h-3 w-3 shrink-0 ${meta.color}`} aria-hidden />
      <span className="text-[10px] text-foreground/90 truncate flex-1">
        {post.caption || 'Untitled post'}
      </span>
    </button>
  );
}


function LegendDot({ label, swatch }) {
  return (
    <div className="flex items-center gap-1">
      <span className={`h-2 w-2 rounded-full ${swatch}`} aria-hidden />
      <span>{label}</span>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Social Post Slide-over (create + edit)
// ═══════════════════════════════════════════════════════════════════════════
function SocialPostSheet({ open, post, defaultDate, onClose, onSaved, canEdit }) {
  const isNew = !post?.id;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showUploader, setShowUploader] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (post?.id) {
      setForm({
        posting_date: post.posting_date || '',
        channel: post.channel || 'LinkedIn',
        caption: post.caption || '',
        hashtags: post.hashtags || '',
        time_of_post: post.time_of_post || '',
        keyword_target: post.keyword_target || '',
        status: post.status || 'Draft',
        creative: post.creative || null,
        sheenas_comments: post.sheenas_comments || '',
      });
    } else {
      setForm({
        posting_date: defaultDate || toIsoDate(new Date()),
        channel: 'LinkedIn',
        caption: '',
        hashtags: '',
        time_of_post: '',
        keyword_target: '',
        status: 'Draft',
        creative: null,
        sheenas_comments: '',
      });
    }
    setShowUploader(false);
  }, [open, post, defaultDate]);

  if (!form) return null;

  const submit = async () => {
    if (!form.posting_date) return toast.error('Posting date is required');
    if (!form.channel) return toast.error('Channel is required');
    setSaving(true);
    try {
      const payload = { ...form, posting_day: dayLabelFromIso(form.posting_date) };
      let res;
      if (isNew) {
        res = await api.post('/social-media-calendar', payload);
        toast.success('Post created');
      } else {
        res = await api.put(`/social-media-calendar/${post.id}`, payload);
        toast.success('Post updated');
      }
      onSaved(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!post?.id) return;
    if (!window.confirm('Delete this social post? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await api.delete(`/social-media-calendar/${post.id}`);
      toast.success('Post deleted');
      onSaved(null);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
    setDeleting(false);
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-card border-white/10" data-testid="social-post-sheet">
        <SheetHeader>
          <SheetTitle>{isNew ? 'New Social Post' : 'Edit Social Post'}</SheetTitle>
          <SheetDescription>
            {canEdit ? 'Compose, schedule and attach creative for a single social post.' : 'Read-only view of this scheduled post.'}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Posting Date" testId="sps-date">
              <Input type="date" value={form.posting_date} disabled={!canEdit} onChange={(e) => setForm({ ...form, posting_date: e.target.value })} className="bg-background border-white/10" data-testid="sps-date-input" />
            </FormField>
            <FormField label="Time" testId="sps-time">
              <Input type="time" value={form.time_of_post} disabled={!canEdit} onChange={(e) => setForm({ ...form, time_of_post: e.target.value })} className="bg-background border-white/10" data-testid="sps-time-input" />
            </FormField>
            <FormField label="Channel" testId="sps-channel">
              <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })} disabled={!canEdit}>
                <SelectTrigger className="bg-background border-white/10" data-testid="sps-channel-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>{SOCIAL_CHANNELS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </FormField>
            <FormField label="Status" testId="sps-status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })} disabled={!canEdit}>
                <SelectTrigger className="bg-background border-white/10" data-testid="sps-status-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>{SOCIAL_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </FormField>
          </div>
          <FormField label="Caption" testId="sps-caption">
            <Textarea rows={5} value={form.caption} disabled={!canEdit} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="bg-background border-white/10" data-testid="sps-caption-input" placeholder="What are you posting?" />
          </FormField>
          <FormField label="Hashtags" testId="sps-hashtags">
            <Input value={form.hashtags} disabled={!canEdit} onChange={(e) => setForm({ ...form, hashtags: e.target.value })} className="bg-background border-white/10" data-testid="sps-hashtags-input" placeholder="#structural #nyc" />
          </FormField>
          <FormField label="Keyword Target" testId="sps-keyword">
            <Input value={form.keyword_target} disabled={!canEdit} onChange={(e) => setForm({ ...form, keyword_target: e.target.value })} className="bg-background border-white/10" data-testid="sps-keyword-input" />
          </FormField>
          <FormField label="Sheena's Comments" testId="sps-comments">
            <Textarea rows={3} value={form.sheenas_comments} disabled={!canEdit} onChange={(e) => setForm({ ...form, sheenas_comments: e.target.value })} className="bg-background border-white/10" data-testid="sps-comments-input" />
          </FormField>

          {/* Creative attachment */}
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground">Creative</Label>
            {form.creative ? (
              <div className="flex items-center gap-3 rounded-md border border-white/10 bg-background/40 px-3 py-2" data-testid="sps-creative-current">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{form.creative.file_name || form.creative.file_id}</p>
                  {form.creative.file_size ? <p className="text-[10px] text-muted-foreground">{(form.creative.file_size / 1024).toFixed(1)} KB</p> : null}
                </div>
                <a href={form.creative.file_url || `/api/files/${form.creative.file_id}`} target="_blank" rel="noreferrer" className="text-[11px] text-primary hover:underline">Open</a>
                {canEdit && (
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setForm(f => ({ ...f, creative: null }))} data-testid="sps-creative-remove">
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ) : canEdit ? (
              showUploader ? (
                <div className="space-y-2">
                  <FileUpload onUploadComplete={(u) => { setForm(f => ({ ...f, creative: u })); setShowUploader(false); }} />
                  <Button size="sm" variant="ghost" onClick={() => setShowUploader(false)} className="text-xs">Cancel upload</Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setShowUploader(true)} className="gap-1.5" data-testid="sps-creative-add">
                  <Plus className="h-3 w-3" /> Add creative
                </Button>
              )
            ) : (
              <p className="text-[11px] text-muted-foreground/60 italic">No creative attached.</p>
            )}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-2">
          <div>
            {!isNew && canEdit && (
              <Button variant="ghost" onClick={remove} disabled={deleting || saving} className="text-destructive hover:text-destructive" data-testid="sps-delete-btn">
                {deleting && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving || deleting}>Close</Button>
            {canEdit && (
              <Button onClick={submit} disabled={saving || deleting} className="bg-primary text-primary-foreground gap-1.5" data-testid="sps-save-btn">
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {isNew ? 'Create' : 'Save'}
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// SECTION 3 - Post Log (all social posts as a data table)
// ═══════════════════════════════════════════════════════════════════════════
function PostLog({ posts, onRowClick }) {
  const rows = useMemo(
    () => [...posts].sort((a, b) => (b.posting_date || '').localeCompare(a.posting_date || '')),
    [posts]
  );

  return (
    <Card className="border-white/10 bg-card shadow-none">
      <CardContent className="p-0">
        <div className="p-4 border-b border-white/10">
          <h3 className="text-sm font-medium uppercase tracking-[0.08em] text-muted-foreground">Post Log</h3>
          <p className="text-[11px] text-muted-foreground/70 mt-0.5">Every social post, most recent first.</p>
        </div>
        {rows.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground text-center">No social posts yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Date</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Day</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Channel</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Caption</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Keyword</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(p => {
                const meta = CHANNEL_META[p.channel] || CHANNEL_META.Other;
                const IconEl = meta.Icon;
                return (
                  <TableRow
                    key={p.id}
                    className="border-white/10 cursor-pointer hover:bg-secondary/30"
                    onClick={() => onRowClick(p)}
                    data-testid={`post-log-row-${p.id}`}
                  >
                    <TableCell className="text-sm font-mono tabular-nums">{formatDate(p.posting_date)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.posting_day || dayLabelFromIso(p.posting_date)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <IconEl className={`h-3.5 w-3.5 ${meta.color}`} aria-hidden />
                        <span className="text-sm">{p.channel || 'Other'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-foreground/90 max-w-[420px] truncate">{p.caption || <span className="italic text-muted-foreground/60">-</span>}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.keyword_target || <span className="italic text-muted-foreground/60">-</span>}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] uppercase ${badgeClass('socialStatus', p.status)}`}>
                        {p.status || 'Draft'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// SECTION 4 - Email Campaign Log (with color-coded rate metrics)
// ═══════════════════════════════════════════════════════════════════════════
function rateColorClass(kind, value) {
  const v = Number(value) || 0;
  if (kind === 'open') {
    if (v >= 20) return 'text-emerald-400';
    if (v >= 10) return 'text-amber-400';
    return 'text-red-400';
  }
  // click
  if (v >= 3) return 'text-emerald-400';
  if (v >= 1) return 'text-amber-400';
  return 'text-red-400';
}


function EmailCampaignLog({ campaigns, canEdit, onAddClick, onRowClick }) {
  const rows = useMemo(
    () => [...campaigns].sort((a, b) => (b.send_date || '').localeCompare(a.send_date || '')),
    [campaigns]
  );

  return (
    <Card className="border-white/10 bg-card shadow-none">
      <CardContent className="p-0">
        <div className="p-4 flex items-center justify-between border-b border-white/10">
          <div>
            <h3 className="text-sm font-medium uppercase tracking-[0.08em] text-muted-foreground">Email Campaign Log</h3>
            <p className="text-[11px] text-muted-foreground/70 mt-0.5">
              Rate colors: green good, amber okay, red under target.
            </p>
          </div>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={onAddClick} className="gap-1.5" data-testid="add-email-campaign-btn">
              <Plus className="h-3.5 w-3.5" /> Add Campaign
            </Button>
          )}
        </div>
        {rows.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground text-center">No email campaigns yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Campaign</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Send Date</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground text-right">Sent</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground text-right">Opens</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground text-right">Open %</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground text-right">Clicks</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground text-right">Click %</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground text-right">Bounces</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground text-right">Unsub</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(c => (
                <TableRow
                  key={c.id}
                  className="border-white/10 cursor-pointer hover:bg-secondary/30"
                  onClick={() => onRowClick(c)}
                  data-testid={`email-log-row-${c.id}`}
                >
                  <TableCell className="text-sm font-medium">{c.campaign_name}</TableCell>
                  <TableCell className="text-sm font-mono tabular-nums text-muted-foreground">{formatDate(c.send_date)}</TableCell>
                  <TableCell className="text-sm font-mono tabular-nums text-right">{(c.total_sent || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-sm font-mono tabular-nums text-right">{(c.opens || 0).toLocaleString()}</TableCell>
                  <TableCell className={`text-sm font-mono tabular-nums text-right font-medium ${rateColorClass('open', c.open_rate)}`} data-testid={`email-log-open-rate-${c.id}`}>
                    {(c.open_rate ?? 0).toFixed(2)}%
                  </TableCell>
                  <TableCell className="text-sm font-mono tabular-nums text-right">{(c.clicks || 0).toLocaleString()}</TableCell>
                  <TableCell className={`text-sm font-mono tabular-nums text-right font-medium ${rateColorClass('click', c.click_rate)}`} data-testid={`email-log-click-rate-${c.id}`}>
                    {(c.click_rate ?? 0).toFixed(2)}%
                  </TableCell>
                  <TableCell className="text-sm font-mono tabular-nums text-right text-muted-foreground">{c.bounces || 0}</TableCell>
                  <TableCell className="text-sm font-mono tabular-nums text-right text-muted-foreground">{c.unsubscribes || 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Email Campaign Modal (create + edit)
// ═══════════════════════════════════════════════════════════════════════════
function EmailCampaignModal({ open, campaign, onClose, onSaved, canEdit }) {
  const isNew = !campaign?.id;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (campaign?.id) {
      setForm({
        campaign_name: campaign.campaign_name || '',
        send_date: campaign.send_date || '',
        total_sent: campaign.total_sent ?? '',
        opens: campaign.opens ?? '',
        clicks: campaign.clicks ?? '',
        bounces: campaign.bounces ?? '',
        unsubscribes: campaign.unsubscribes ?? '',
        notes: campaign.notes || '',
      });
    } else {
      setForm({
        campaign_name: '', send_date: toIsoDate(new Date()),
        total_sent: '', opens: '', clicks: '', bounces: '', unsubscribes: '', notes: '',
      });
    }
  }, [open, campaign]);

  if (!form) return null;

  // Live-derived rates for the form (backend also computes on save).
  const total = Number(form.total_sent) || 0;
  const opens = Number(form.opens) || 0;
  const clicks = Number(form.clicks) || 0;
  const openRate = total > 0 ? (opens / total) * 100 : 0;
  const clickRate = total > 0 ? (clicks / total) * 100 : 0;

  const submit = async () => {
    if (!form.campaign_name.trim()) return toast.error('Campaign name is required');
    setSaving(true);
    try {
      const payload = {
        campaign_name: form.campaign_name,
        send_date: form.send_date,
        total_sent: Number(form.total_sent) || 0,
        opens: Number(form.opens) || 0,
        clicks: Number(form.clicks) || 0,
        bounces: Number(form.bounces) || 0,
        unsubscribes: Number(form.unsubscribes) || 0,
        notes: form.notes,
      };
      const res = isNew
        ? await api.post('/email-campaigns', payload)
        : await api.put(`/email-campaigns/${campaign.id}`, payload);
      toast.success(isNew ? 'Campaign created' : 'Campaign saved');
      onSaved(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!campaign?.id) return;
    if (!window.confirm('Delete this campaign? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await api.delete(`/email-campaigns/${campaign.id}`);
      toast.success('Campaign deleted');
      onSaved(null);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
    setDeleting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="email-campaign-modal">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New Email Campaign' : 'Edit Email Campaign'}</DialogTitle>
          <DialogDescription>Track send counts and engagement. Rates are computed automatically.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormField label="Campaign Name" testId="ec-name" className="md:col-span-2">
            <Input value={form.campaign_name} disabled={!canEdit} onChange={(e) => setForm({ ...form, campaign_name: e.target.value })} className="bg-background border-white/10" data-testid="ec-name-input" placeholder="Spring 2026 Newsletter" />
          </FormField>
          <FormField label="Send Date" testId="ec-date">
            <Input type="date" value={form.send_date} disabled={!canEdit} onChange={(e) => setForm({ ...form, send_date: e.target.value })} className="bg-background border-white/10" data-testid="ec-date-input" />
          </FormField>
          <FormField label="Total Sent" testId="ec-total">
            <Input type="number" min="0" value={form.total_sent} disabled={!canEdit} onChange={(e) => setForm({ ...form, total_sent: e.target.value })} className="bg-background border-white/10" data-testid="ec-total-input" />
          </FormField>
          <FormField label="Opens" testId="ec-opens">
            <Input type="number" min="0" value={form.opens} disabled={!canEdit} onChange={(e) => setForm({ ...form, opens: e.target.value })} className="bg-background border-white/10" data-testid="ec-opens-input" />
          </FormField>
          <FormField label="Clicks" testId="ec-clicks">
            <Input type="number" min="0" value={form.clicks} disabled={!canEdit} onChange={(e) => setForm({ ...form, clicks: e.target.value })} className="bg-background border-white/10" data-testid="ec-clicks-input" />
          </FormField>
          <FormField label="Bounces" testId="ec-bounces">
            <Input type="number" min="0" value={form.bounces} disabled={!canEdit} onChange={(e) => setForm({ ...form, bounces: e.target.value })} className="bg-background border-white/10" data-testid="ec-bounces-input" />
          </FormField>
          <FormField label="Unsubscribes" testId="ec-unsub">
            <Input type="number" min="0" value={form.unsubscribes} disabled={!canEdit} onChange={(e) => setForm({ ...form, unsubscribes: e.target.value })} className="bg-background border-white/10" data-testid="ec-unsub-input" />
          </FormField>
          <FormField label="Notes" testId="ec-notes" className="md:col-span-2">
            <Textarea rows={3} value={form.notes} disabled={!canEdit} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-background border-white/10" data-testid="ec-notes-input" />
          </FormField>

          {/* Derived rates preview */}
          <div className="md:col-span-2 grid grid-cols-2 gap-3 pt-2 border-t border-white/5">
            <div className="rounded-md border border-white/10 bg-background/40 p-3" data-testid="ec-open-preview">
              <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Open Rate (auto)</p>
              <p className={`text-lg font-mono tabular-nums font-medium ${rateColorClass('open', openRate)} mt-1`}>
                {openRate.toFixed(2)}%
              </p>
            </div>
            <div className="rounded-md border border-white/10 bg-background/40 p-3" data-testid="ec-click-preview">
              <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Click Rate (auto)</p>
              <p className={`text-lg font-mono tabular-nums font-medium ${rateColorClass('click', clickRate)} mt-1`}>
                {clickRate.toFixed(2)}%
              </p>
            </div>
          </div>
        </div>
        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <div>
            {!isNew && canEdit && (
              <Button variant="ghost" onClick={remove} disabled={saving || deleting} className="text-destructive hover:text-destructive" data-testid="ec-delete-btn">
                {deleting && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving || deleting}>Close</Button>
            {canEdit && (
              <Button onClick={submit} disabled={saving || deleting} className="bg-primary text-primary-foreground gap-1.5" data-testid="ec-save-btn">
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {isNew ? 'Create' : 'Save'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Utility form components
// ═══════════════════════════════════════════════════════════════════════════
function FormField({ label, testId, className = '', children }) {
  return (
    <div className={`space-y-1 ${className}`} data-testid={`field-${testId}`}>
      <Label className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}


function SectionHeader({ label }) {
  return (
    <div className="flex items-center gap-2 pt-2">
      <div className="h-px flex-1 bg-white/5" aria-hidden />
      <p className="text-[11px] uppercase tracking-[0.12em] font-medium text-muted-foreground">{label}</p>
      <div className="h-px flex-1 bg-white/5" aria-hidden />
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// PAGE COMPOSITION
// ═══════════════════════════════════════════════════════════════════════════
export default function ContentSocialPage() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm('manage_digital_marketing');

  const [content, setContent] = useState([]);
  const [social, setSocial] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal / sheet state
  const [detailCard, setDetailCard] = useState(null);
  const [socialSheet, setSocialSheet] = useState({ open: false, post: null, defaultDate: '' });
  const [campaignModal, setCampaignModal] = useState({ open: false, campaign: null });
  const [anchor, setAnchor] = useState(new Date());

  const reloadAll = async () => {
    try {
      const [c, s, e, k, st] = await Promise.all([
        api.get('/content-calendar'),
        api.get('/social-media-calendar'),
        api.get('/email-campaigns'),
        api.get('/keywords'),
        api.get('/monthly-strategies'),
      ]);
      setContent(c.data || []);
      setSocial(s.data || []);
      setCampaigns(e.data || []);
      setKeywords(k.data || []);
      setStrategies(st.data || []);
    } catch (err) {
      console.error('Content & Social fetch failed', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reloadAll(); }, []);

  const handleContentStatusChange = async (cardId, newStatus) => {
    const card = content.find(c => c.id === cardId);
    if (!card) return;
    setContent(prev => prev.map(c => (c.id === cardId ? { ...c, status: newStatus } : c)));
    try {
      const payload = { ...card, status: newStatus };
      delete payload._id;
      await api.put(`/content-calendar/${cardId}`, payload);
      toast.success(`Moved to "${newStatus}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
      setContent(prev => prev.map(c => (c.id === cardId ? card : c)));
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="content-social-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-md bg-primary/10 border border-primary/20">
          <CalIcon className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-[22px] font-medium tracking-tight" data-testid="content-social-title">Content & Social</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Plan content requests, schedule social posts, and track email campaign performance in one place.</p>
        </div>
      </div>

      {/* Section 1 - Content Requests Kanban */}
      <div className="flex items-baseline justify-between pt-2">
        <div className="flex items-center gap-2 flex-1">
          <div className="h-px flex-1 bg-white/5" aria-hidden />
          <p className="text-[11px] uppercase tracking-[0.12em] font-medium text-muted-foreground">Content Requests</p>
          <div className="h-px flex-1 bg-white/5" aria-hidden />
        </div>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            className="ml-3 gap-1.5"
            onClick={() => setDetailCard({})}
            data-testid="content-request-new-btn"
          >
            <Plus className="h-3.5 w-3.5" /> New Request
          </Button>
        )}
      </div>
      {loading ? (
        <SkeletonBlock />
      ) : (
        <ContentKanban
          cards={content}
          canEdit={canEdit}
          onCardClick={(c) => setDetailCard(c)}
          onCardStatusChange={handleContentStatusChange}
        />
      )}

      {/* Section 2 - Social Media Calendar */}
      <div className="flex items-baseline justify-between pt-2">
        <div className="flex items-center gap-2 flex-1">
          <div className="h-px flex-1 bg-white/5" aria-hidden />
          <p className="text-[11px] uppercase tracking-[0.12em] font-medium text-muted-foreground">Social Media Calendar</p>
          <div className="h-px flex-1 bg-white/5" aria-hidden />
        </div>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            className="ml-3 gap-1.5"
            onClick={() => setSocialSheet({ open: true, post: null, defaultDate: toIsoDate(new Date()) })}
            data-testid="social-cal-new-btn"
          >
            <Plus className="h-3.5 w-3.5" /> New Post
          </Button>
        )}
      </div>
      <SocialMediaCalendar
        posts={social}
        anchor={anchor}
        setAnchor={setAnchor}
        canEdit={canEdit}
        onCellCreate={(iso) => setSocialSheet({ open: true, post: null, defaultDate: iso })}
        onPostClick={(post) => setSocialSheet({ open: true, post, defaultDate: '' })}
      />

      {/* Section 3 - Post Log */}
      <SectionHeader label="Post Log" />
      <PostLog
        posts={social}
        onRowClick={(post) => setSocialSheet({ open: true, post, defaultDate: '' })}
      />

      {/* Section 4 - Email Campaign Log */}
      <div className="flex items-center gap-2 pt-2">
        <div className="h-px flex-1 bg-white/5" aria-hidden />
        <div className="flex items-center gap-1.5">
          <Mail className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          <p className="text-[11px] uppercase tracking-[0.12em] font-medium text-muted-foreground">Email Campaigns</p>
        </div>
        <div className="h-px flex-1 bg-white/5" aria-hidden />
      </div>
      <EmailCampaignLog
        campaigns={campaigns}
        canEdit={canEdit}
        onAddClick={() => setCampaignModal({ open: true, campaign: null })}
        onRowClick={(c) => setCampaignModal({ open: true, campaign: c })}
      />

      {/* Modals & Sheets */}
      <ContentDetailModal
        open={!!detailCard}
        card={detailCard}
        canEdit={canEdit}
        keywords={keywords}
        strategies={strategies}
        onClose={() => setDetailCard(null)}
        onSaved={() => reloadAll()}
      />
      <SocialPostSheet
        open={socialSheet.open}
        post={socialSheet.post}
        defaultDate={socialSheet.defaultDate}
        canEdit={canEdit}
        onClose={() => setSocialSheet({ open: false, post: null, defaultDate: '' })}
        onSaved={() => reloadAll()}
      />
      <EmailCampaignModal
        open={campaignModal.open}
        campaign={campaignModal.campaign}
        canEdit={canEdit}
        onClose={() => setCampaignModal({ open: false, campaign: null })}
        onSaved={() => reloadAll()}
      />
    </div>
  );
}


function SkeletonBlock() {
  return (
    <div className="rounded-md border border-white/10 bg-card/40 p-4">
      <div className="h-4 bg-white/5 rounded w-1/4 animate-pulse mb-3" />
      <div className="h-3 bg-white/5 rounded w-2/3 animate-pulse mb-2" />
      <div className="h-3 bg-white/5 rounded w-1/2 animate-pulse" />
    </div>
  );
}
